import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Zap, Target, Star, RefreshCw, Trophy, Volume2, VolumeX } from 'lucide-react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { useTTS } from '../hooks/useTTS';

const KEYBOARD_LAYOUT = [
  { row: 0, keys: ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'] },
  { row: 1, keys: ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'] },
  { row: 2, keys: ['Z', 'X', 'C', 'V', 'B', 'N', 'M'] },
  { row: 3, keys: [' '] }
];

// Two-handed typing practice words (alternating hands)
const TWO_HANDED_WORDS = [
  'zoom', 'jump', 'play', 'work', 'type', 'land', 'fish', 'barn', 'duck', 'fork',
  'help', 'open', 'shut', 'turn', 'push', 'pull', 'walk', 'talk', 'sing', 'swim',
  'quiz', 'jazz', 'fizz', 'buzz', 'exam', 'flex', 'next', 'text', 'exit', 'taxi',
  'bank', 'tank', 'rank', 'yank', 'junk', 'funk', 'punk', 'dunk', 'bunk', 'hunk',
  'palm', 'calm', 'balm', 'film', 'helm', 'yelp', 'kelp', 'help', 'gulp', 'pulp'
];

// Finger assignments (1=index, 2=middle, 3=ring, 4=pinky)
const FINGER_MAP = {
  'Q': 4, 'A': 4, 'Z': 4,
  'W': 3, 'S': 3, 'X': 3,
  'E': 2, 'D': 2, 'C': 2,
  'R': 1, 'F': 1, 'V': 1, 'T': 1, 'G': 1, 'B': 1,
  'Y': 1, 'H': 1, 'N': 1, 'U': 1, 'J': 1, 'M': 1,
  'I': 2, 'K': 2,
  'O': 3, 'L': 3,
  'P': 4,
  ' ': 1
};

const FINGER_COLORS = {
  1: 'bg-red-500/60 border-red-500',
  2: 'bg-yellow-500/60 border-yellow-500',
  3: 'bg-green-500/60 border-green-500',
  4: 'bg-purple-500/60 border-purple-500'
};

const TypingPracticePage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const { speakAsPlayer, stop: stopTTS, isPlaying: isTTSPlaying } = useTTS();
  
  const [phrases, setPhrases] = useState([]);
  const [currentPhrase, setCurrentPhrase] = useState('');
  const [typedText, setTypedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [keystrokes, setKeystrokes] = useState(0);
  const [correctKeystrokes, setCorrectKeystrokes] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [activeKey, setActiveKey] = useState('');
  const [lastKeyCorrect, setLastKeyCorrect] = useState(null);
  const [sessionResult, setSessionResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voiceEnabled, setVoiceEnabled] = useState(user?.voice_enabled !== false);

  useEffect(() => {
    fetchPhrases();
  }, [user]);

  const fetchPhrases = async () => {
    try {
      const stage = user?.stage || 1;
      const response = await axios.get(`${API}/typing/phrases/${stage}`);
      // Mix in two-handed practice words
      const regularPhrases = response.data.phrases || [];
      const mixedPhrases = [...regularPhrases, ...TWO_HANDED_WORDS.slice(0, 10)];
      setPhrases(mixedPhrases);
      selectRandomPhrase(mixedPhrases);
    } catch (err) {
      console.error('Failed to fetch phrases:', err);
      // Fallback to two-handed words
      setPhrases(TWO_HANDED_WORDS);
      selectRandomPhrase(TWO_HANDED_WORDS);
    } finally {
      setLoading(false);
    }
  };

  const selectRandomPhrase = (phraseList) => {
    const phrase = phraseList[Math.floor(Math.random() * phraseList.length)];
    setCurrentPhrase(phrase.toLowerCase());
    resetSession();
    
    // Read the phrase aloud if voice is enabled
    if (voiceEnabled && user?.voice_enabled !== false) {
      setTimeout(() => {
        speakAsPlayer(phrase, user?.character_voice || 'alice');
      }, 500);
    }
  };

  const resetSession = () => {
    setTypedText('');
    setCurrentIndex(0);
    setKeystrokes(0);
    setCorrectKeystrokes(0);
    setStartTime(null);
    setIsComplete(false);
    setLastKeyCorrect(null);
    setSessionResult(null);
    stopTTS(); // Stop any playing audio
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // Read current phrase aloud
  const readAloud = () => {
    if (currentPhrase) {
      speakAsPlayer(currentPhrase, user?.character_voice || 'alice');
    }
  };

  // Handle virtual keyboard key press
  const handleVirtualKeyPress = (key) => {
    if (isComplete) return;
    
    const char = key === ' ' ? ' ' : key.toLowerCase();
    const expectedChar = currentPhrase[currentIndex];
    
    if (!startTime) {
      setStartTime(Date.now());
    }
    
    setActiveKey(key);
    setTimeout(() => setActiveKey(''), 150);
    setKeystrokes(prev => prev + 1);
    
    if (char === expectedChar) {
      setCorrectKeystrokes(prev => prev + 1);
      setLastKeyCorrect(true);
      setTypedText(prev => prev + char);
      setCurrentIndex(prev => prev + 1);
      
      // Check if phrase is complete
      if (currentIndex + 1 >= currentPhrase.length) {
        completeSession();
      }
    } else {
      setLastKeyCorrect(false);
    }
    
    // Keep focus on input for physical keyboard
    inputRef.current?.focus();
  };
  
  const completeSession = async () => {
    const accuracy = keystrokes > 0 ? Math.round((correctKeystrokes / keystrokes) * 100) : 100;
    const creditsEarned = Math.max(1, Math.floor(accuracy / 20));
    setSessionResult({ accuracy, credits_earned: creditsEarned });
    setIsComplete(true);
    
    // Save to backend
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.post(`${API}/typing/session`, {
        phrase: currentPhrase,
        accuracy,
        keystrokes,
        correct_keystrokes: correctKeystrokes,
        time_ms: Date.now() - startTime
      }, { headers: { Authorization: `Bearer ${token}` } });
      refreshUser?.();
    } catch (err) {
      console.error('Failed to save session:', err);
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (isComplete) return;
    
    const key = e.key;
    if (key.length === 1 || key === ' ') {
      setActiveKey(key.toUpperCase());
      setTimeout(() => setActiveKey(''), 150);
    }
  }, [isComplete]);

  const handleInput = async (e) => {
    if (isComplete) return;
    
    const newText = e.target.value.toLowerCase();
    const expectedChar = currentPhrase[currentIndex];
    const typedChar = newText[newText.length - 1];
    
    if (!startTime) {
      setStartTime(Date.now());
    }
    
    setKeystrokes(prev => prev + 1);
    
    if (typedChar === expectedChar) {
      setCorrectKeystrokes(prev => prev + 1);
      setLastKeyCorrect(true);
      setTypedText(newText);
      setCurrentIndex(prev => prev + 1);
      
      // Check if complete
      if (newText.length === currentPhrase.length) {
        handleSessionComplete(newText);
      }
    } else {
      setLastKeyCorrect(false);
      // Don't advance - show error
    }
  };

  const handleSessionComplete = async (finalText) => {
    setIsComplete(true);
    const duration = (Date.now() - startTime) / 1000;
    const accuracy = keystrokes > 0 ? Math.round(((correctKeystrokes + 1) / (keystrokes + 1)) * 100) : 100;
    const creditsEarned = Math.max(1, Math.floor(accuracy / 20));
    setSessionResult({ accuracy, credits_earned: creditsEarned });
    
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.post(`${API}/typing/session`, {
        phrase: currentPhrase,
        typed_text: finalText,
        keystrokes: keystrokes + 1,
        correct_keystrokes: correctKeystrokes + 1,
        duration_seconds: duration
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      refreshUser?.();
    } catch (err) {
      console.error('Error saving session:', err);
    }
  };

  const nextPhrase = () => {
    selectRandomPhrase(phrases);
  };

  const accuracy = keystrokes > 0 ? Math.round((correctKeystrokes / keystrokes) * 100) : 100;
  const nextKey = currentPhrase[currentIndex]?.toUpperCase() || '';

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-space flex items-center justify-center">
        <div className="text-bot-cyan animate-pulse font-display text-2xl">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-space">
      <div className="grid-bg" />
      
      {/* Top Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-panel/90 backdrop-blur-md border-b border-mid-panel">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-screen-white/60 hover:text-screen-white" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-body">Back</span>
          </Link>
          
          <div className="font-display text-xl">
            <span className="text-bot-cyan">Type</span>
            <span className="text-zap-yellow">Bots</span>
            <span className="text-screen-white/50 ml-2">PRACTICE</span>
          </div>
          
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
              accuracy >= 90 ? 'bg-bot-cyan/20 text-bot-cyan' :
              accuracy >= 70 ? 'bg-zap-yellow/20 text-zap-yellow' :
              'bg-glitch-red/20 text-glitch-red'
            }`}>
              <Target className="w-4 h-4" />
              <span className="font-mono text-sm" data-testid="accuracy-display">{accuracy}%</span>
            </div>
          </div>
        </div>
      </nav>
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Phrase Display */}
          <div className="bg-dark-panel border-2 border-mid-panel rounded-xl p-8 mb-8" data-testid="phrase-display">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-bot-cyan rounded-full animate-pulse" />
                <span className="font-mono text-xs text-bot-cyan">// TYPE THE PHRASE BELOW</span>
              </div>
              
              {/* Voice Controls */}
              <div className="flex items-center gap-2">
                <button
                  onClick={readAloud}
                  disabled={isTTSPlaying}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${
                    isTTSPlaying 
                      ? 'bg-mint-green/20 text-mint-green' 
                      : 'bg-lavender/20 text-lavender hover:bg-lavender/30'
                  }`}
                  title="Read aloud"
                >
                  <Volume2 className="w-4 h-4" />
                  <span className="hidden sm:inline">{isTTSPlaying ? 'Playing...' : 'Listen'}</span>
                </button>
                <button
                  onClick={() => setVoiceEnabled(!voiceEnabled)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    voiceEnabled 
                      ? 'bg-mint-green/20 text-mint-green' 
                      : 'bg-gray-600/50 text-gray-400'
                  }`}
                  title={voiceEnabled ? 'Voice on' : 'Voice off'}
                >
                  {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="font-mono text-3xl md:text-4xl tracking-wide">
              {currentPhrase.split('').map((char, i) => (
                <span
                  key={i}
                  className={`${
                    i < currentIndex ? 'text-bot-cyan' :
                    i === currentIndex ? 'bg-zap-yellow/30 text-zap-yellow border-b-2 border-zap-yellow' :
                    'text-screen-white/30'
                  }`}
                >
                  {char === ' ' ? '\u00A0' : char}
                </span>
              ))}
            </div>
            
            {/* Hidden input for keyboard typing */}
            <input
              ref={inputRef}
              type="text"
              value={typedText}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              className="sr-only"
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
              data-testid="typing-input"
            />
            
            {/* Tap to focus message */}
            {!isComplete && (
              <button
                onClick={() => inputRef.current?.focus()}
                className="w-full mt-4 py-3 border border-mid-panel rounded-lg text-screen-white/50 font-body text-sm hover:border-bot-cyan hover:text-bot-cyan transition-colors md:block hidden"
              >
                Click here or start typing...
              </button>
            )}
            
            {/* Mobile instruction */}
            {!isComplete && (
              <p className="mt-4 text-center text-screen-white/50 text-sm md:hidden">
                Tap the keys below to type
              </p>
            )}
          </div>
          
          {/* Virtual Keyboard */}
          <div className="bg-dark-panel border-2 border-mid-panel rounded-xl p-6 mb-8" data-testid="keyboard">
            <div className="mb-4 flex items-center justify-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-500/60" />
                <span className="font-mono text-xs text-screen-white/50">Index</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-yellow-500/60" />
                <span className="font-mono text-xs text-screen-white/50">Middle</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-500/60" />
                <span className="font-mono text-xs text-screen-white/50">Ring</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-purple-500/60" />
                <span className="font-mono text-xs text-screen-white/50">Pinky</span>
              </div>
            </div>
            
            {KEYBOARD_LAYOUT.map((row, rowIndex) => (
              <div key={rowIndex} className={`flex justify-center gap-1 mb-1 ${rowIndex === 1 ? 'ml-4' : rowIndex === 2 ? 'ml-8' : ''}`}>
                {row.keys.map((key) => {
                  const finger = FINGER_MAP[key];
                  const isActive = activeKey === key;
                  const isNext = nextKey === key;
                  const isSpace = key === ' ';
                  
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleVirtualKeyPress(key)}
                      className={`
                        ${isSpace ? 'w-32 sm:w-48' : 'w-10 sm:w-12'} h-10 sm:h-12 rounded-lg flex items-center justify-center
                        font-mono text-xs sm:text-sm font-bold transition-all duration-200
                        border-2 ${FINGER_COLORS[finger]}
                        ${isActive ? 'scale-95 brightness-150' : ''}
                        ${isNext ? 'ring-2 ring-zap-yellow ring-offset-1 sm:ring-offset-2 ring-offset-deep-space scale-110 shadow-lg shadow-zap-yellow/30' : ''}
                        ${lastKeyCorrect === false && isNext ? 'bg-glitch-red/60 border-glitch-red' : ''}
                        hover:brightness-125 active:scale-95 cursor-pointer select-none
                      `}
                      style={isNext ? { animation: 'gentle-pulse 1.5s ease-in-out infinite' } : {}}
                      data-testid={`key-${key === ' ' ? 'space' : key}`}
                    >
                      {isSpace ? 'SPACE' : key}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          
          {/* Session Complete */}
          {isComplete && sessionResult && (
            <div className="bg-dark-panel border-2 border-bot-cyan rounded-xl p-8 text-center" data-testid="session-complete">
              <div className="mb-6">
                <Zap className="w-16 h-16 text-zap-yellow mx-auto mb-4" />
                <h2 className="font-display text-3xl text-screen-white mb-2">PHRASE COMPLETE!</h2>
                <p className="font-body text-screen-white/60">Great job zapping that glitch!</p>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="bg-deep-space rounded-lg p-4">
                  <Target className="w-6 h-6 text-bot-cyan mx-auto mb-2" />
                  <div className="font-display text-2xl text-bot-cyan">{sessionResult.accuracy}%</div>
                  <div className="font-mono text-xs text-screen-white/50">ACCURACY</div>
                </div>
                <div className="bg-deep-space rounded-lg p-4">
                  <Star className="w-6 h-6 text-zap-yellow mx-auto mb-2" />
                  <div className="font-display text-2xl text-zap-yellow">+{sessionResult.credits_earned}</div>
                  <div className="font-mono text-xs text-screen-white/50">CREDITS</div>
                </div>
                <div className="bg-deep-space rounded-lg p-4">
                  <Trophy className="w-6 h-6 text-bot-cyan mx-auto mb-2" />
                  <div className="font-display text-2xl text-screen-white">Stage {sessionResult.new_stage}</div>
                  <div className="font-mono text-xs text-screen-white/50">LEVEL</div>
                </div>
              </div>
              
              {sessionResult.stage_changed && (
                <div className="bg-zap-yellow/20 border border-zap-yellow rounded-lg p-4 mb-6">
                  <p className="font-display text-xl text-zap-yellow">
                    LEVEL UP! You reached Stage {sessionResult.new_stage}!
                  </p>
                </div>
              )}
              
              <div className="flex gap-4 justify-center">
                <button
                  onClick={nextPhrase}
                  className="font-display uppercase tracking-wider bg-zap-yellow text-deep-space px-8 py-3 rounded-sm hover:translate-y-1 hover:shadow-none shadow-[0_4px_0_0_#B2A000] transition-all flex items-center gap-2"
                  data-testid="next-phrase-btn"
                >
                  <RefreshCw className="w-5 h-5" />
                  Next Phrase
                </button>
                <Link
                  to="/dashboard"
                  className="font-display uppercase tracking-wider bg-transparent text-bot-cyan border-2 border-bot-cyan px-8 py-3 rounded-sm hover:bg-bot-cyan/10 transition-colors"
                  data-testid="back-dashboard-btn"
                >
                  Dashboard
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TypingPracticePage;
